/**
 * Getting in (Figma 10 · "10 Getting in"): the parts every public page is made
 * of. Values from the frames' layer data: fields 48 tall at r12 with a 13/18
 * sentence-case label above (library `Field · Email` 3:971), buttons 48 pills
 * with Manrope 600 15 (3:987 primary, 3:1001 glass), alerts r12 pad 12/14 with
 * a 15 glyph (3:457), the status message's 44 glyph box (3:433).
 */
import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import {
  CircleAlert,
  Eye,
  EyeOff,
  Lock,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitButtonSpinner, kitButtonClass } from "@/app/routes/dashboard/components/kit/Button";
import { kitFieldInvalidClass, kitFieldSurfaceClass } from "@/app/routes/dashboard/components/kit/Field";
import GoogleIcon from "@/assets/icons/sso/google.svg?react";

/* ---------------------------------------------------------------- brand */

/** Logo / Wordmark (94:4632): محتوى in KO Rubbama 28 over Mohtawa in Crake 700 28, gap -2. */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn("gi-wordmark", className)} aria-label="Mohtawa">
      <span aria-hidden className="gi-wordmark-ar">
        محتوى
      </span>
      <span aria-hidden className="gi-wordmark-en">
        Mohtawa
      </span>
    </span>
  );
}

/** Top (94:4635): the wordmark, a 1×24 divider, "Creo", and one quiet action at the far end. */
export function TopBar({ action }: { action?: ReactNode }) {
  return (
    <header className="gi-top">
      <Link to="/" className="unstyled-link flex items-center gap-3.5" aria-label="Creo home">
        <Wordmark />
        <span aria-hidden className="h-6 w-px bg-co-line2" />
        <span className="text-[15px] font-semibold leading-5 text-co-text">Creo</span>
      </Link>
      <span className="flex-1" />
      {action}
    </header>
  );
}

/** Greeting (94:4646): the page's one brand moment, مرحباً بك in lime. */
export function Greeting() {
  return (
    <p className="gi-greeting" lang="ar" dir="rtl">
      مرحباً بك
    </p>
  );
}

/* ---------------------------------------------------------------- heads */

/** Head (63:4793): Crake 400 44/51, a 16/24 line under it. */
export function Head({ title, sub, align = "center" }: { title: ReactNode; sub?: ReactNode; align?: "center" | "start" }) {
  return (
    <div className={cn("flex w-full flex-col gap-3.5", align === "center" ? "items-center text-center" : "items-start")}>
      <h1 className="gi-title">{title}</h1>
      {sub ? <p className="text-[16px] leading-6 text-co-text2">{sub}</p> : null}
    </div>
  );
}

/** Message (3:433): a 44 glyph box, Barlow Condensed 800 32/34, and a 15/23 body. */
export function StatusHead({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-3.5 text-center">
      <span className="grid size-11 place-items-center rounded-card border border-co-line2 text-co-text">
        <Icon aria-hidden className="size-5" strokeWidth={1.75} />
      </span>
      <h1 className="gi-status-title">{title}</h1>
      {children ? <div className="text-[15px] leading-[23px] text-co-muted">{children}</div> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- notices */

export type NoticeTone = "danger" | "attention" | "info";

/**
 * Alert (3:457): r12, pad 12/14, gap 10, a 15 glyph and Manrope 13/20. Danger
 * draws the danger stroke; info a hairline; attention a hairline with a danger
 * glyph. Arrives on the snappy spring (M01), never shakes.
 */
export function Notice({ tone, icon: Icon, children }: { tone: NoticeTone; icon?: LucideIcon; children: ReactNode }) {
  const Glyph = Icon ?? CircleAlert;
  return (
    <div
      role={tone === "info" ? "status" : "alert"}
      className={cn(
        "gi-notice flex w-full items-start gap-2.5 rounded-card border px-3.5 py-3 text-left",
        tone === "danger" ? "border-co-danger" : "border-co-line2",
      )}
    >
      <Glyph
        aria-hidden
        className={cn("mt-[2px] size-[15px] shrink-0", tone === "info" ? "text-co-text2" : "text-co-danger")}
        strokeWidth={2}
      />
      <p className="text-[13px] leading-5 text-co-text">{children}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- fields */

export interface GiFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  /** Stated before typing; replaced in place by the error (M02). */
  hint?: ReactNode;
  error?: string | null;
  /** Read-only, with a lock: the address an invitation or a session is for. */
  locked?: boolean;
  /** Password field with the eye. */
  secret?: boolean;
}

/** Field (3:971): label 13/18 above, a 48 field at r12, value 16/22, hint 12.5/18 below. */
export const GiField = forwardRef<HTMLInputElement, GiFieldProps>(
  ({ label, hint, error, locked, secret, id, className, ...props }, ref) => {
    const generated = useId();
    const inputId = id ?? generated;
    const hintId = `${inputId}-hint`;
    const [visible, setVisible] = useState(false);
    const invalid = Boolean(error);
    return (
      <div className={cn("flex w-full flex-col gap-2", className)}>
        <label htmlFor={inputId} className="text-[13px] font-medium leading-[18px] text-co-text2">
          {label}
        </label>
        <div
          data-invalid={invalid || undefined}
          className={cn(
            "co-text-field group flex h-12 items-center gap-2.5 pl-4 pr-2 transition-[outline-color] duration-[180ms]",
            kitFieldSurfaceClass,
            invalid && kitFieldInvalidClass,
          )}
        >
          <input
            ref={ref}
            id={inputId}
            readOnly={locked || props.readOnly}
            aria-invalid={invalid || undefined}
            aria-describedby={hint || error ? hintId : undefined}
            {...props}
            type={secret ? (visible ? "text" : "password") : props.type}
            className={cn(
              "co-input-own-focus caret-co-accent h-full min-w-0 flex-1 bg-transparent text-[16px] leading-[22px] text-co-text placeholder:text-co-faint outline-none focus-visible:!shadow-none",
              locked && "cursor-default",
            )}
          />
          {locked ? (
            <span className="grid size-9 shrink-0 place-items-center text-co-muted" aria-hidden>
              <Lock className="size-4" strokeWidth={1.75} />
            </span>
          ) : secret ? (
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide password" : "Show password"}
              aria-pressed={visible}
              className="grid size-9 shrink-0 place-items-center rounded-full text-co-muted transition-colors hover:bg-co-hover hover:text-co-text"
            >
              {visible ? <EyeOff aria-hidden className="size-4" /> : <Eye aria-hidden className="size-4" />}
            </button>
          ) : null}
        </div>
        {error || hint ? (
          <span
            id={hintId}
            className={cn(
              "text-[12.5px] leading-[18px] transition-colors duration-[180ms]",
              invalid ? "text-co-danger" : "text-co-muted",
            )}
          >
            {error || hint}
          </span>
        ) : null}
      </div>
    );
  },
);
GiField.displayName = "GiField";

/* ---------------------------------------------------------------- actions */

interface GiButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary";
  loading?: boolean;
  icon?: ReactNode;
}

/** Buttons (3:987 / 3:1001): 48 pills, Manrope 600 15, full width of the column. */
export function GiButton({ variant = "primary", loading, icon, className, children, disabled, ...props }: GiButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled}
      aria-busy={loading || undefined}
      className={cn(
        kitButtonClass({ variant, size: "lg" }),
        "h-12 w-full gap-2.5 px-[22px] text-[15px] font-semibold",
        className,
      )}
    >
      {loading ? <KitButtonSpinner /> : icon}
      <span>{children}</span>
    </button>
  );
}

export function GoogleButton(props: Omit<GiButtonProps, "variant" | "icon" | "children"> & { label?: string }) {
  const { label = "Continue with Google", ...rest } = props;
  return (
    <GiButton variant="secondary" icon={<GoogleIcon aria-hidden className="size-[18px]" />} {...rest}>
      {label}
    </GiButton>
  );
}

/** Or (3:1057): two hairlines either side of "or", 12.5 muted, gap 14. */
export function OrDivider() {
  return (
    <div className="flex w-full items-center gap-3.5" role="separator">
      <span className="h-px flex-1 bg-co-line2" />
      <span className="text-[12.5px] leading-[17px] text-co-muted">or</span>
      <span className="h-px flex-1 bg-co-line2" />
    </div>
  );
}

/** Text action (3:233): 32 tall, pad 0/4, Manrope 500 13. A link or a button. */
export function QuietAction({
  to,
  onClick,
  children,
  className,
  type = "button",
}: {
  to?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  type?: "button" | "submit";
}) {
  const cls = cn(
    "unstyled-link inline-flex h-8 items-center rounded-pill px-1 text-[13px] font-medium leading-[18px] text-co-text2 transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent",
    className,
  );
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

/** Agreement: the terms said under the button, with links (D4 in the journey). */
export function Agreement({ verb = "joining" }: { verb?: string }) {
  return (
    <p className="text-[12.5px] leading-[18px] text-co-muted">
      By {verb} you agree to the
      <br />
      <Link to="/terms-of-service" className="unstyled-link font-medium text-co-text2 hover:text-co-text">
        Terms of Service
      </Link>{" "}
      and the{" "}
      <Link to="/privacy-policy" className="unstyled-link font-medium text-co-text2 hover:text-co-text">
        Privacy Policy
      </Link>
    </p>
  );
}

/** Footer (3:1037): © year Mohtawa · Privacy Policy · Terms of Service, 12.5. */
export function Footer() {
  return (
    <footer className="flex flex-wrap items-center justify-center gap-2 text-[12.5px] leading-[17px]">
      <span className="text-co-faint">© {new Date().getFullYear()} Mohtawa</span>
      <Link to="/privacy-policy" className="unstyled-link px-1 py-2 text-co-muted hover:text-co-text">
        Privacy Policy
      </Link>
      <Link to="/terms-of-service" className="unstyled-link px-1 py-2 text-co-muted hover:text-co-text">
        Terms of Service
      </Link>
    </footer>
  );
}

/** The 400-wide column every public page centres. */
export function Column({ children, gap = 24 }: { children: ReactNode; gap?: number }) {
  return (
    <div className="gi-column" style={{ gap }}>
      {children}
    </div>
  );
}

/** A stack of the ways forward (Ways: gap 16). */
export function Ways({ children }: { children: ReactNode }) {
  return <div className="flex w-full flex-col gap-4">{children}</div>;
}

/** Note (3:1263): one quiet line, 12.5/19 muted. */
export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn("gi-fade w-full text-center text-[12.5px] leading-[19px] text-co-muted", className)}>{children}</p>;
}
